import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Stack,
  Switch,
  Collapse,
  Divider,
  IconButton,
  Alert,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';

import StepModify from './otherComponent/StepModify';
import CommonNodeSetting from './otherComponent/CommonNodeSetting/CommonNodeSetting';
import useApi from 'hooks/useApi';
import {
  ReasonsEditor,
  FieldStateSelect,
  ResumeCaptureEditor,
  PhotoSlotsEditor,
  emptyResumeCfg,
  syncLegacyPhotoKeys,
  subCardSx,
  PlannedCfg,
  ImmediateCfg,
  SwapCfg,
  ResumeCfg,
} from './otherComponent/Driver/DriverActionEditors';

type StatusItem = { _id: string; name: string; uid: string };
type NotificationPayload = { conditions: string[]; users: string[]; roles: string[]; channels: string[] };

type DriverActions = { rest: boolean; stop: boolean; swap: boolean; driving: boolean };
type DrivingCfg = { instruction: string; arriveLabel: string; allowTakeControl: boolean };

type DriverFormProps = {
  selectedNodeData: any;
  selectedNode: any;
  onSubmit: (data: any) => void;
  onClose: () => void;
};

const DEFAULT_PLANNED_REASONS = [
  'Scheduled rest break',
  'Refuelling',
  'Traffic or weather delay',
  'Awaiting loading / unloading',
  'End of shift',
];
const DEFAULT_IMMEDIATE_REASONS = [
  'Vehicle breakdown',
  'Accident / collision',
  'Unsafe road conditions',
  'Medical emergency',
  'Security / theft concern',
];
const DEFAULT_SWAP_REASONS = [
  'Shift change / handover',
  'Driver unavailable',
  'Reassigned to another route',
  'Personal emergency',
];

/**
 * Driver's Component config.
 *
 * The single place Rest / Stop / Swap are configured (they used to be
 * process-level "Transaction Controls"), plus the "I'm Driving" control
 * handover. Everything lands in `component.settings`, which the backend stores
 * as Mixed and validates as `Joi.object().unknown(true)` — so no schema work is
 * needed for any of it.
 *
 * `actions.driving` decides how a driver gets PAST this step:
 *   on  → "I'm Driving" then "Reached the Destination" completes it
 *   off → a plain "Continue" completes it
 * Rest / Stop / Swap never complete the step; they suspend or leave.
 */
const DriverForm = memo(({ selectedNodeData, onSubmit, onClose, selectedNode }: DriverFormProps) => {
  const [initialValues, setInitialValues] = useState<any | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [statuses, setStatuses] = useState<StatusItem[]>([]);
  const [notificationPayload, setNotificationPayload] = useState<NotificationPayload>({
    conditions: [],
    users: [],
    roles: [],
    channels: [],
  });
  const [escalationPayload, setEscalationPayload] = useState<any>({
    videoCall: { enabled: false, actors: [], users: [], callType: 'VIDEO' },
    chat: { enabled: false, actors: [], users: [] },
  });

  // ── Driver config, held outside Formik ──
  // These are nested arrays/objects edited by dedicated child components; keeping
  // them in local state (as the old settings page did) avoids threading
  // setFieldValue through every editor for no gain.
  const [actions, setActions] = useState<DriverActions>({ rest: true, stop: true, swap: true, driving: true });
  const [planned, setPlanned] = useState<PlannedCfg>({ reasons: [], dateTime: 'optional' });
  const [immediate, setImmediate] = useState<ImmediateCfg>({ reasons: [], photo: 'required', photos: [] });
  const [swap, setSwap] = useState<SwapCfg>({ reasons: [] });
  const [restResume, setRestResume] = useState<ResumeCfg>(emptyResumeCfg());
  const [stopResume, setStopResume] = useState<ResumeCfg>(emptyResumeCfg());
  // Whether Rest / Stop / Swap stay usable for the driver who is NOT holding
  // the wheel. On a two-driver run the co-driver resting while the other drives
  // is the normal case, so this defaults on.
  const [allowActionsWhileWaiting, setAllowActionsWhileWaiting] = useState(true);
  const [drivingCfg, setDrivingCfg] = useState<DrivingCfg>({
    instruction: '',
    arriveLabel: 'Reached the Destination',
    allowTakeControl: true,
  });

  const { uploadAsset, showToast } = useApi();

  const handleStatusesLoaded = useCallback((list: StatusItem[]) => setStatuses(list), []);
  const handleNotificationChange = useCallback((payload: NotificationPayload) => setNotificationPayload(payload), []);
  const handleEscalationChange = useCallback((payload: any) => setEscalationPayload(payload), []);

  const handleStepTypeChange = useCallback((newType: string, setFieldValue: any, submitForm: any) => {
    const type = newType.toUpperCase();
    setFieldValue('stepType', type);
    setFieldValue('componentType', type);
    setFieldValue('data.title', type);
    setTimeout(() => submitForm());
  }, []);

  useEffect(() => {
    if (!selectedNodeData) return;

    const {
      name = selectedNodeData.component?.name || '',
      title,
      description,
      settings,
      component = {},
      componentType,
      data,
      onComplete,
    } = selectedNodeData;

    const actor = selectedNode?.data?.step?.actor?.map((a: any) => a?.uid) ?? [];

    // A brand-new DRIVER step arrives with the backend defaults from
    // DRIVER_COMPONENT_SETTINGS; a step converted from another type arrives with
    // that type's settings, so every branch falls back to a usable default.
    setActions({
      rest: settings?.actions?.rest ?? true,
      stop: settings?.actions?.stop ?? true,
      swap: settings?.actions?.swap ?? true,
      driving: settings?.actions?.driving ?? true,
    });
    setPlanned({
      reasons: settings?.plannedStopConfig?.reasons ?? DEFAULT_PLANNED_REASONS,
      dateTime: settings?.plannedStopConfig?.dateTime ?? 'optional',
    });
    setImmediate({
      reasons: settings?.immediateStopConfig?.reasons ?? DEFAULT_IMMEDIATE_REASONS,
      photo: settings?.immediateStopConfig?.photo ?? 'required',
      photos: settings?.immediateStopConfig?.photos ?? [],
    });
    setSwap({ reasons: settings?.swapConfig?.reasons ?? DEFAULT_SWAP_REASONS });
    setRestResume({ ...emptyResumeCfg(), ...(settings?.restResumeConfig ?? {}) });
    setStopResume({ ...emptyResumeCfg(), ...(settings?.stopResumeConfig ?? {}) });
    setAllowActionsWhileWaiting(settings?.allowActionsWhileWaiting ?? true);
    setDrivingCfg({
      instruction: settings?.drivingConfig?.instruction ?? '',
      arriveLabel: settings?.drivingConfig?.arriveLabel || 'Reached the Destination',
      allowTakeControl: settings?.drivingConfig?.allowTakeControl ?? true,
    });

    setInitialValues({
      name: name || "Driver's Component",
      title: title || "Driver's Component",
      description: description || '',
      component,
      componentType: componentType || 'DRIVER',
      data: {
        title: selectedNode?.data?.label || data?.title || "Driver's Component",
        step: { ...(data?.step || {}), actor, componentType: componentType || 'DRIVER' },
      },
      actor,
      stepType: selectedNode?.data?.step?.stepType || 'DRIVER',
      onCompleteStatusUid: selectedNode?.data?.step?.onComplete?.status?.uid || '',
      onComplete: onComplete || {},
    });

    const backendNotification = selectedNode?.data?.step?.notification || {};
    setNotificationPayload({
      conditions: backendNotification.conditions || [],
      users: backendNotification.users || [],
      roles: backendNotification.roles || [],
      channels: backendNotification.channels || [],
    });

    // Pass the stored object straight through. This used to be rebuilt by hand
    // from a fixed list of keys, which silently dropped any field it did not
    // know about — including `inherit`, so opening and saving any step would
    // reset it to the process default.
    setEscalationPayload(selectedNode?.data?.step?.escalation || {});
  }, [
    selectedNodeData?.componentType,
    selectedNodeData?.name,
    selectedNodeData?.description,
    selectedNodeData?.settings,
    selectedNode?.data?.label,
    selectedNode?.data?.step?.actor,
    selectedNode?.data?.step?.notification,
    selectedNode?.data?.step?.escalation,
    selectedNode?.data?.step?.onComplete?.status?.uid,
  ]);

  const notificationInitialValues = useMemo(() => {
    const backendNotification = selectedNode?.data?.step?.notification || {};
    return {
      scheduleOnETA: backendNotification.conditions?.includes('ON_ETA') || false,
      onCompletion: backendNotification.conditions?.includes('ON_COMPLETE') || false,
      notifyRoles: backendNotification.roles || [],
      notifyUsers: backendNotification.users || [],
      notificationChannel: backendNotification.channels || [],
    };
  }, [selectedNode?.data?.step?.notification]);

  const toggleRow = (label: string, description: string, value: boolean, onChange: (v: boolean) => void) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1.5,
        px: 2,
        borderRadius: 2,
        bgcolor: value ? 'rgba(30,142,78,0.06)' : 'grey.50',
        border: '1px solid',
        borderColor: value ? 'rgba(30,142,78,0.25)' : 'divider',
      }}
    >
      <Box sx={{ pr: 2 }}>
        <Typography fontWeight={600}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      </Box>
      <Switch checked={value} onChange={(e) => onChange(e.target.checked)} color="success" />
    </Box>
  );

  if (!initialValues) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography>Loading Driver's Component…</Typography>
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        const selectedStatus = statuses.find((s) => s.uid === values.onCompleteStatusUid);

        const payload = {
          ...values,
          settings: {
            actions,
            plannedStopConfig: planned,
            // `syncLegacyPhotoKeys` derives the old photo/maxPhotos pair from the
            // slots on the way out — an app build predating slots reads only
            // those, so leaving them stale would ask for the wrong photo count.
            immediateStopConfig: syncLegacyPhotoKeys(immediate),
            swapConfig: swap,
            restResumeConfig: syncLegacyPhotoKeys(restResume),
            stopResumeConfig: syncLegacyPhotoKeys(stopResume),
            allowActionsWhileWaiting,
            drivingConfig: drivingCfg,
          },
          component: {
            ...values.component,
            name: values.name,
            componentType: 'DRIVER',
            // The Driver's Component has no user-authored fields — its whole
            // surface comes from `settings`.
            fields: [],
          },
          onComplete: selectedStatus ? { status: selectedStatus } : {},
        };

        onSubmit({ ...payload, notification: notificationPayload, escalation: escalationPayload });
        setSubmitting(false);
      }}
    >
      {({ values, handleChange, handleBlur, handleSubmit, setFieldValue, submitForm }) => (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Paper elevation={0} sx={{ p: 2, width: '100%' }}>
            <Box display="flex" justifyContent="center" alignItems="center" mb={3} borderBottom={1} borderColor="grey.400" pb={2}>
              {isEditingTitle ? (
                <Box display="flex" alignItems="center" sx={{ width: '100%', maxWidth: '600px' }}>
                  <TextField
                    name="data.title"
                    value={values.data?.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoFocus
                    fullWidth
                    sx={{ mr: 2, textTransform: 'uppercase' }}
                  />
                  <Button variant="contained" size="small" onClick={() => setIsEditingTitle(false)}>
                    save
                  </Button>
                </Box>
              ) : (
                <Box display="flex" alignItems="center" sx={{ width: '100%', justifyContent: 'space-between' }}>
                  <Box display="flex" alignItems="center" sx={{ gap: 1 }}>
                    <Typography
                      variant="h5"
                      fontWeight={500}
                      sx={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'uppercase' }}
                      title={values.data?.title || values.stepType}
                    >
                      {values.data?.title || "Driver's Component"}
                    </Typography>
                    <IconButton size="small" onClick={() => setIsEditingTitle(true)} sx={{ p: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {selectedNode && (
                    <Box sx={{ minWidth: '120px', minHeight: '36px' }}>
                      <StepModify
                        selectedNode={selectedNode}
                        onStepTypeChange={(newType) => handleStepTypeChange(newType, setFieldValue, submitForm)}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    The driver sees this step instead of a normal transaction step. Only the actions enabled below
                    are shown. Rest, Stop and Swap pause or leave the transaction — they never complete this step.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Stack spacing={1.5}>
                    {/* ── Driving ── */}
                    {toggleRow(
                      "I'm Driving",
                      'One driver takes control; the others are held on this step until the destination is reached.',
                      actions.driving,
                      (driving) => setActions({ ...actions, driving }),
                    )}
                    <Collapse in={actions.driving} unmountOnExit>
                      <Box sx={{ ...subCardSx, ml: 1, mb: 1 }}>
                        <Stack spacing={2}>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            label="Instruction (optional)"
                            placeholder="e.g. Confirm the load is secured before setting off."
                            value={drivingCfg.instruction}
                            onChange={(e) => setDrivingCfg({ ...drivingCfg, instruction: e.target.value })}
                          />
                          <TextField
                            size="small"
                            fullWidth
                            label="Arrival button label"
                            value={drivingCfg.arriveLabel}
                            onChange={(e) => setDrivingCfg({ ...drivingCfg, arriveLabel: e.target.value })}
                            helperText="Pressing this releases control and completes the step for everyone."
                          />
                          <Divider />
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Box sx={{ pr: 2 }}>
                              <Typography fontWeight={600}>Waiting driver can Rest / Stop / Swap</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Lets the driver who is not holding the wheel still use the actions above — the
                                usual case when a co-driver rests while the other drives. Turn off to hold them
                                on this step with no actions until control is released.
                              </Typography>
                            </Box>
                            <Switch
                              checked={allowActionsWhileWaiting}
                              onChange={(e) => setAllowActionsWhileWaiting(e.target.checked)}
                              color="success"
                            />
                          </Box>
                          <Divider />
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                            <Box sx={{ pr: 2 }}>
                              <Typography fontWeight={600}>Allow Take Control</Typography>
                              <Typography variant="caption" color="text.secondary">
                                A waiting driver can take the wheel from whoever holds it. Turn off to make control
                                releasable only by the driver holding it.
                              </Typography>
                            </Box>
                            <Switch
                              checked={drivingCfg.allowTakeControl}
                              onChange={(e) => setDrivingCfg({ ...drivingCfg, allowTakeControl: e.target.checked })}
                              color="success"
                            />
                          </Box>
                        </Stack>
                      </Box>
                    </Collapse>

                    {!actions.driving && (
                      <Alert severity="info" sx={{ ml: 1 }}>
                        With <strong>I'm Driving</strong> off, the driver completes this step with a plain{' '}
                        <strong>Continue</strong> button — useful when the component is only a Rest / Stop hub.
                      </Alert>
                    )}

                    {/* ── Rest ── */}
                    {toggleRow('Take a Rest', 'Driver can pause the transaction and resume later.', actions.rest, (rest) =>
                      setActions({ ...actions, rest }),
                    )}
                    <Collapse in={actions.rest} unmountOnExit>
                      <Box sx={{ pl: 1, pb: 1 }}>
                        <ResumeCaptureEditor cfg={restResume} onChange={setRestResume} uploadAsset={uploadAsset} showToast={showToast} />
                      </Box>
                    </Collapse>

                    {/* ── Stop ── */}
                    {toggleRow('Stop Transaction', 'Driver can stop the transaction (planned or immediate).', actions.stop, (stop) =>
                      setActions({ ...actions, stop }),
                    )}
                    <Collapse in={actions.stop} unmountOnExit>
                      <Stack spacing={2} sx={{ pl: 1, pb: 1 }}>
                        <Box sx={subCardSx}>
                          <Typography fontWeight={700} mb={1}>Planned Stop</Typography>
                          <ReasonsEditor reasons={planned.reasons} onChange={(reasons) => setPlanned({ ...planned, reasons })} />
                          <Divider sx={{ my: 1.5 }} />
                          <FieldStateSelect
                            label="Planned date & time"
                            value={planned.dateTime}
                            onChange={(dateTime) => setPlanned({ ...planned, dateTime })}
                          />
                        </Box>
                        <Box sx={subCardSx}>
                          <Typography fontWeight={700} mb={1}>Immediate Stop</Typography>
                          <ReasonsEditor reasons={immediate.reasons} onChange={(reasons) => setImmediate({ ...immediate, reasons })} />
                          <Divider sx={{ my: 1.5 }} />
                          <Typography fontWeight={600} mb={0.5}>Evidence photos</Typography>
                          <PhotoSlotsEditor
                            photos={immediate.photos}
                            onChange={(photos) => setImmediate({ ...immediate, photos })}
                            legacyHint="No titled photos — the driver just gets an untitled camera tile, using the setting below."
                          />
                          {/* Only governs capture while no slots are defined; with
                              slots it is derived on save for older app builds. */}
                          {immediate.photos.length === 0 && (
                            <>
                              <Divider sx={{ my: 1.5 }} />
                              <FieldStateSelect
                                label="Evidence photos"
                                value={immediate.photo}
                                onChange={(photo) => setImmediate({ ...immediate, photo })}
                              />
                            </>
                          )}
                        </Box>
                        <ResumeCaptureEditor cfg={stopResume} onChange={setStopResume} uploadAsset={uploadAsset} showToast={showToast} />
                      </Stack>
                    </Collapse>

                    {/* ── Swap ── */}
                    {toggleRow('Swap Transaction', 'Driver can hand the transaction over to another driver.', actions.swap, (s) =>
                      setActions({ ...actions, swap: s }),
                    )}
                    <Collapse in={actions.swap} unmountOnExit>
                      <Box sx={{ ...subCardSx, ml: 1, mb: 1 }}>
                        <Typography fontWeight={700} mb={1}>Swap reasons</Typography>
                        <ReasonsEditor reasons={swap.reasons} onChange={(reasons) => setSwap({ reasons })} />
                      </Box>
                    </Collapse>
                  </Stack>
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <CommonNodeSetting
                    selectedNode={selectedNode}
                    onStatusesLoaded={handleStatusesLoaded}
                    onNotificationChange={handleNotificationChange}
                    notificationInitialValues={notificationInitialValues}
                    escalationPayload={escalationPayload}
                    onEscalationChange={handleEscalationChange}
                  />
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
                <Button type="button" color="error" sx={{ py: 1, fontSize: '1rem' }} onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary" sx={{ py: 1, fontSize: '1rem' }}>
                  Save &amp; Close
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}
    </Formik>
  );
});

export default DriverForm;
